import React from 'react';
import ParticleBackground from './ParticleBackground';
import { Helmet } from 'react-helmet-async';

const brand = {
  nearBlack: '#141318',
  darkGray: '#1F2937',
  light: '#E9EAE8',
  white: '#FFFFFF',
  blueGray: '#566D87',
  gold: '#FFD404',
  ink: '#00181C',
};

const sectionStyle = (bg = brand.nearBlack) => ({
  backgroundColor: bg,
  color: brand.light,
  fontFamily: "'Inter', sans-serif",
});

const h2Style = {
  fontFamily: "'Audiowide', sans-serif",
  color: brand.blueGray,
  fontSize: 'clamp(1.6rem, 5vw, 2.3rem)',
  marginBottom: '0.5rem',
  textAlign: 'center',
};

const pLeadStyle = {
  color: brand.light,
  fontSize: 'clamp(0.95rem, 2.8vw, 1.1rem)',
  opacity: 0.95,
  textAlign: 'center',
  maxWidth: '900px',
  margin: '0.25rem auto 0',
};

const btnStyle = {
  backgroundColor: brand.blueGray,
  color: brand.white,
  border: 'none',
  padding: '12px 18px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '999px',
  textDecoration: 'none',
  display: 'inline-block',
  transition: 'transform 180ms ease, filter 180ms ease',
};

const cardBase = {
  backgroundColor: '#1B1A20',
  border: `1px solid ${brand.blueGray}33`,
  borderRadius: 16,
  padding: 20,
  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
};

const gridResponsive = (colsLg) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${colsLg}, minmax(0, 1fr))`,
  gap: 24,
  marginTop: 24,
});

const outer = (bg) => ({
  background: `radial-gradient(1000px 500px at 10% -10%, ${brand.gold}0D, transparent 70%), radial-gradient(900px 500px at 110% 10%, ${brand.blueGray}0D, transparent 70%), ${bg}`,
  width: '100%'
});
const inner = { maxWidth: '1200px', margin: '0 auto', padding: '64px 24px' };

export default function GrowthSections() {
  return (
    <div style={{ ...sectionStyle(brand.nearBlack), position: 'relative', overflow: 'hidden' }}>
      <ParticleBackground className="opacity-25" />
      {/* FAQ JSON-LD for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Do I need to pay online?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No. Payment is collected at the venue on the day of your reservation.'
                }
              },
              {
                '@type': 'Question',
                name: 'How early should I arrive?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Please arrive 10 minutes early for check-in, gear setup, and briefing.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I book a private party?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. You can book private sessions and birthday packages tailored for groups in Cornwall, ON.'
                }
              },
              {
                '@type': 'Question',
                name: 'Where are you located?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '917 Second St W, Cornwall, ON K6J 1H8.'
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Limited-time Promo */}
      <section style={{ ...outer(brand.darkGray), borderTop: `1px solid ${brand.blueGray}33`, borderBottom: `1px solid ${brand.blueGray}33` }}>
        <div style={{ ...inner, textAlign: 'center' }}>
          <h2 style={h2Style}>Weekday Special — Lock Your Slot Today</h2>
          <div style={{ width: 64, height: 3, margin: '8px auto 12px', backgroundColor: brand.gold }} />
          <p style={pLeadStyle}>Reserve a private session Monday–Thursday and secure our best availability. Pay at the venue. Limited slots each day.</p>
          {/* Keep a single primary CTA here; avoid extra buttons elsewhere */}
          <div style={{ marginTop: 16 }}>
            <a
              href="/pricing"
              className="btn"
              style={{
                ...btnStyle,
                backgroundColor: brand.gold,
                color: brand.ink,
                boxShadow: '0 6px 18px rgba(255, 212, 4, 0.25)'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              onFocus={(e) => (e.currentTarget.style.boxShadow = '0 8px 22px rgba(255, 212, 4, 0.35)')}
              onBlur={(e) => (e.currentTarget.style.boxShadow = '0 6px 18px rgba(255, 212, 4, 0.25)')}
            >
              Book Your Game in Seconds
            </a>
            <div style={{ color: brand.light, opacity: 0.8, fontSize: 12, marginTop: 8 }}>
              No prepayment required — pay at the arena.
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ ...outer(brand.nearBlack) }}>
        <div style={inner}>
          <h2 style={h2Style}>Why Choose Laser Zone Arena</h2>
          <div style={{ width: 64, height: 3, margin: '8px auto 12px', backgroundColor: brand.gold }} />
          <p style={pLeadStyle}>Premier laser tag in Cornwall, ON — modern gear, immersive missions, and flexible booking for families, friends, and teams.</p>
          <div style={{ ...gridResponsive(3), marginTop: 32 }}>
          {[
            { title: 'Immersive Missions', text: 'Team battles, solo challenges, and themed objectives that keep the action intense.' },
            { title: 'Private & Public Games', text: 'Book exclusive sessions or jump into a public match — your choice.' },
            { title: 'Party-Ready Venue', text: 'Birthday and group packages with lounge space and easy on-site payment.' },
          ].map((f, i) => (
              <a
                key={i}
                href="/pricing"
                style={{ ...cardBase, textDecoration: 'none', display: 'block', position: 'relative', overflow: 'hidden' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,0.35)';
                  e.currentTarget.style.borderColor = `${brand.gold}66`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = `${brand.blueGray}33`;
                }}
              >
              <h3 style={{ margin: '4px 0 8px', color: brand.white, fontWeight: 700 }}>{f.title}</h3>
              <p style={{ margin: 0, color: brand.light }}>{f.text}</p>
                <span style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: `inset 0 0 60px ${brand.blueGray}22` }} />
              </a>
          ))}
          </div>
          {/* Removed extra CTA here per request */}
        </div>
      </section>

      {/* How It Works */}
      <section style={{ ...outer(brand.darkGray) }}>
        <div style={{ ...inner, padding: '88px 24px' }}>
          <h2 style={h2Style}>How It Works</h2>
          <div style={{ width: 64, height: 3, margin: '8px auto 12px', backgroundColor: brand.gold }} />
          <p style={pLeadStyle}>Booking takes under 2 minutes. Follow these steps and you’re set — no prepayment required.</p>
          <div style={{ ...gridResponsive(4), gap: 18, marginTop: 20 }}>
          {[
            { step: '1', title: 'Pick a Mode', text: 'Choose a game from our Pricing page.' },
            { step: '2', title: 'Select Time', text: 'Find a slot that works for your group.' },
            { step: '3', title: 'Arrive Early', text: 'Show up 10 minutes before your match.' },
            { step: '4', title: 'Pay On-Site', text: 'Payment is made at the arena on the day.' },
          ].map((s, i) => (
              <a
                key={i}
                href="/pricing"
                style={{ ...cardBase, textAlign: 'left', textDecoration: 'none', display: 'block', position: 'relative', overflow: 'hidden' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,0.35)';
                  e.currentTarget.style.borderColor = `${brand.gold}66`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = `${brand.blueGray}33`;
                }}
              >
              <div style={{ color: brand.blueGray, fontWeight: 800, fontSize: 18, marginBottom: 6 }}>Step {s.step}</div>
              <div style={{ color: brand.white, fontWeight: 700 }}>{s.title}</div>
              <div style={{ color: brand.light, opacity: 0.95 }}>{s.text}</div>
                <span style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: `inset 0 0 60px ${brand.blueGray}22` }} />
              </a>
          ))}
          </div>
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <div style={{ color: brand.light, fontSize: '1.05rem', marginBottom: 10 }}>Shall we start with the first step?</div>
            <a
              href="/pricing"
              className="btn"
              style={{
                ...btnStyle,
                backgroundColor: brand.gold,
                color: brand.ink,
                boxShadow: '0 6px 18px rgba(255, 212, 4, 0.25)'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              onFocus={(e) => (e.currentTarget.style.boxShadow = '0 8px 22px rgba(255, 212, 4, 0.35)')}
              onBlur={(e) => (e.currentTarget.style.boxShadow = '0 6px 18px rgba(255, 212, 4, 0.25)')}
            >
              Begin Booking Now
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section style={{ ...outer(brand.nearBlack) }}>
        <div style={inner}>
          <h2 style={h2Style}>Loved by Cornwall Players</h2>
          <div style={{ width: 64, height: 3, margin: '8px auto 12px', backgroundColor: brand.gold }} />
          <div style={gridResponsive(3)}>
          {[
            { name: 'Ava M.', text: 'Perfect birthday spot — staff made it smooth and exciting!' },
            { name: 'Liam R.', text: 'Great missions and fair scoring. We’ll be back next weekend.' },
            { name: 'Noah D.', text: 'The private session was totally worth it for our team.' },
          ].map((t, i) => (
              <div
                key={i}
                style={cardBase}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 22px rgba(0,0,0,0.35)';
                  e.currentTarget.style.borderColor = `${brand.blueGray}66`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = `${brand.blueGray}33`;
                }}
              >
              <div style={{ color: brand.white, fontWeight: 700, marginBottom: 6 }}>{t.name}</div>
              <div style={{ color: brand.light }}>{t.text}</div>
            </div>
          ))}
          </div>
          {/* Removed extra CTA here per request */}
        </div>
      </section>

      {/* FAQ Preview */}
      <section style={{ ...outer(brand.darkGray) }}>
        <div style={inner}>
          <h2 style={h2Style}>FAQ</h2>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {[
            { q: 'Do I need to pay online?', a: 'No — payment is made at the venue on the day of your reservation.' },
            { q: 'Is there a minimum number of players?', a: 'You can join a public room alone or bring a group. Private sessions are great for larger groups.' },
            { q: 'Do you host birthdays?', a: 'Yes, we offer party-ready options and lounge space. Contact us for availability.' },
          ].map((item, i) => (
              <details
                key={i}
                style={{ background: '#1B1A20', border: `1px solid ${brand.blueGray}33`, borderRadius: 12, padding: 16, marginBottom: 12, transition: 'border-color 200ms ease, background 200ms ease' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${brand.blueGray}66`;
                  e.currentTarget.style.background = '#1E1D23';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = `${brand.blueGray}33`;
                  e.currentTarget.style.background = '#1B1A20';
                }}
              >
                <summary
                  style={{ color: brand.white, fontWeight: 700, cursor: 'pointer', outline: 'none' }}
                  onFocus={(e) => (e.currentTarget.parentElement.style.borderColor = `${brand.blueGray}AA`)}
                  onBlur={(e) => (e.currentTarget.parentElement.style.borderColor = `${brand.blueGray}33`)}
                >
                  {item.q}
                </summary>
              <p style={{ color: brand.light, marginTop: 10 }}>{item.a}</p>
            </details>
          ))}
          </div>
          {/* Removed extra CTA here per request */}
        </div>
      </section>
    </div>
  );
}
